import { gpcEnabled } from "./privacy";
/**
 * First-party marketing attribution capture.
 *
 * On initial load, reads UTM params, click IDs (fbclid, gclid, ttclid),
 * landing page, first_seen timestamp, and GA client ID from the _ga cookie.
 * Stores under localStorage key "aerwear_attribution".
 * Never overwrites existing stored values with empty ones.
 */

const STORAGE_KEY = "aerwear_attribution";

const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"];
const CLICK_ID_KEYS = ["fbclid", "gclid", "ttclid"];
const ALL_PARAM_KEYS = [...UTM_KEYS, ...CLICK_ID_KEYS];

/**
 * Extract GA client ID from the _ga cookie.
 * _ga cookie format: GA1.1.123456789.1234567890
 * The client ID is the last two dot-separated segments.
 */
function getGaClientId() {
  const match = document.cookie.match(/_ga=GA\d+\.\d+\.([^;]+)/);
  return match ? match[1] : null;
}

/**
 * Read stored attribution from localStorage.
 */
export function getAttribution() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
  } catch {
    return {};
  }
}

/**
 * Capture attribution from URL params on page load.
 * Merges with existing stored values — never overwrites with empty.
 */
export function captureAttribution() {
  const params = new URLSearchParams(window.location.search);
  const existing = getAttribution();
  const updates = {};

  // Capture UTM + click ID params from URL
  for (const key of ALL_PARAM_KEYS) {
    const value = params.get(key);
    if (value) {
      updates[key] = value;
    }
  }

  // Landing page — only set on first visit
  if (!existing.landing_page) {
    updates.landing_page = window.location.pathname;
  }

  // First seen timestamp — only set on first visit
  if (!existing.first_seen) {
    updates.first_seen = new Date().toISOString();
  }

  // GA client ID from _ga cookie
  const gaClientId = getGaClientId();
  if (gaClientId) {
    updates.ga_client_id = gaClientId;
  }

  // Merge: only set keys that have non-empty values, never overwrite with empty
  const merged = { ...existing };
  for (const [key, value] of Object.entries(updates)) {
    if (value) {
      merged[key] = value;
    }
  }

  localStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
  return merged;
}

/**
 * Get attribution data formatted for the checkout API call.
 * Returns an object of non-empty key-value pairs.
 */
export function getCheckoutAttribution() {
  const data = getAttribution();
  const attrs = {};
  const keys = [...UTM_KEYS, "fbclid", "gclid", "ttclid", "landing_page", "ga_client_id"];
  for (const key of keys) {
    if (data[key]) {
      attrs[key] = data[key];
    }
  }
  // Tag the order so the backend skips the Meta Purchase event for GPC visitors.
  if (gpcEnabled()) attrs.gpc = "1";
  return attrs;
}

/**
 * Build UTM query string to append to checkout URL.
 */
export function getUtmQueryString() {
  const data = getAttribution();
  const params = new URLSearchParams();
  for (const key of UTM_KEYS) {
    if (data[key]) {
      params.set(key, data[key]);
    }
  }
  const qs = params.toString();
  return qs ? qs : "";
}
